import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customerService, CustomerFilters, CreateCustomerData, UpdateCustomerData } from '../services';

export const useCustomers = (filters?: CustomerFilters) => {
  const queryClient = useQueryClient();

  // Get customers query
  const { data, isLoading, error } = useQuery({
    queryKey: ['customers', filters],
    queryFn: async () => {
      const response = await customerService.getCustomers(filters);
      return response;
    },
  });

  // Create customer mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateCustomerData) => customerService.createCustomer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  // Update customer mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCustomerData }) =>
      customerService.updateCustomer(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  // Delete customer mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => customerService.deleteCustomer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  return {
    customers: data,
    isLoading,
    error,
    createCustomer: createMutation.mutateAsync,
    updateCustomer: updateMutation.mutateAsync,
    deleteCustomer: deleteMutation.mutateAsync,
    createMutation,
    updateMutation,
    deleteMutation,
  };
};

export const useCustomer = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['customer', id],
    queryFn: async () => {
      const response = await customerService.getCustomer(id);
      return response.data;
    },
    enabled: !!id,
  });

  return {
    customer: data,
    isLoading,
    error,
  };
};
